<script setup lang="ts">
import { SwitchComponent } from '@metapic-apps/shared'
import { computed, watch } from 'vue'

import { useAdvertiserGroupsRequest } from '@/api'
import CampaignFormAdvertiserSelector from '@/components/CampaignForm/CampaignFormTargetBlock/CampaignFormAdvertiserSelector.vue'
import CampaignFormPreregisterUsersInput from '@/components/CampaignForm/CampaignFormTargetBlock/CampaignFormPreregisterUsersInput.vue'
import TargetInput from '@/components/TargetInput/TargetInput.vue'
import type { CampaignFormType } from '@/lib/types/campaign.types'
import { TargetItemEnum } from '@/lib/types/common.types'
import { BoxComponent, FlexComponent, SelectComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{ isEditMode: boolean }>()

const advertiserGroup = defineModel<CampaignFormType['advertiserGroup']>('advertiserGroup', { required: true })
const advertiserList = defineModel<CampaignFormType['advertiserList']>('advertiserList')
const hasTargetAllCreators = defineModel<CampaignFormType['hasTargetAllCreators']>('hasTargetAllCreators')
const hasPreregisterCreators = defineModel<CampaignFormType['hasPreregisterCreators']>('hasPreregisterCreators')
const targets = defineModel<CampaignFormType['targets']>('targets', { required: true })
const preregisterItems = defineModel<CampaignFormType['preregisterItems']>('preregisterItems', { required: true })
const hasUnmatchedTargets = defineModel<CampaignFormType['hasUnmatchedTargets']>('hasUnmatchedTargets')
const currency = defineModel<string>('currency')

const showTagsTab = props.isEditMode && targets.value.some((target) => target.type === TargetItemEnum.UserTag)
const visibleTargetTabs = showTagsTab
  ? [TargetItemEnum.User, TargetItemEnum.UserTag, TargetItemEnum.Client, TargetItemEnum.Segment]
  : [TargetItemEnum.User, TargetItemEnum.Client, TargetItemEnum.Segment]

const { data: advertiserGroups, isLoading: isAdvertiserGroupsLoading } = useAdvertiserGroupsRequest()
const advertiserGroupsOptions = computed(() => advertiserGroups.value.map((item) => ({ label: item.name, value: item.id })))

watch(
  () => advertiserGroup.value,
  () => {
    if (!props.isEditMode) {
      advertiserList.value = []
      targets.value = []
      preregisterItems.value = []
    }
  },
)

watch(
  [() => advertiserGroup.value, () => advertiserGroups.value],
  ([value]) => {
    if (value) {
      currency.value = advertiserGroups.value.find((group) => group.id === value)?.currency
    }
  },
  { immediate: true },
)
</script>
<template>
  <FlexComponent :gap="24" direction="column">
    <SelectComponent
      v-if="advertiserGroup"
      v-model="advertiserGroup"
      title="Advertiser group"
      filterable
      name="advertiserGroup"
      :options="advertiserGroupsOptions"
      :disabled="isEditMode"
      :is-fetching="isAdvertiserGroupsLoading"
    />
    <CampaignFormAdvertiserSelector v-model="advertiserList" :advertiser-group="advertiserGroup" :is-edit-mode="isEditMode" />

    <SwitchComponent v-model="hasTargetAllCreators" title="Target all creators of advertiser group" name="hasTargetAllCreators" />

    <TargetInput
      v-if="!hasTargetAllCreators && advertiserGroup"
      v-model:targets="targets"
      v-model:has-unmatched-targets="hasUnmatchedTargets"
      :advertiser-group="advertiserGroup"
      :is-edit-mode="isEditMode"
      :visible-tabs="visibleTargetTabs"
      name="targets"
      title="Target specific audience"
    />
    <SwitchComponent v-model="hasPreregisterCreators" title="Pre-register creators" name="hasPreregisterCreators">
      <template #tooltip>
        <BoxComponent :w="280">
          <BoxComponent :mb="4">
            <TypographyComponent variant="title-medium">Pre-register creators</TypographyComponent>
          </BoxComponent>
          <TypographyComponent variant="body-medium">
            Creators who register with the specified email or Instagram username will be automatically added to this campaign. You can use
            this, for instance, if you're in direct contact (DM) with creators.
          </TypographyComponent>
        </BoxComponent>
      </template>
    </SwitchComponent>
    <CampaignFormPreregisterUsersInput v-if="hasPreregisterCreators" v-model="preregisterItems" />
  </FlexComponent>
</template>
